import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../services/firebaseConfig";
import { FiUserPlus, FiPercent, FiUsers, FiTrendingUp, FiLogOut } from "react-icons/fi";
import InsuranceForm from "./InsuranceForm"
import "./AgentDashboard.css";
import { Helmet } from "react-helmet";


<Helmet>
  <title>Agent Dashboard | Kora Insurance </title>
  <meta name="description" content="Protect your family with Kora's flexible and affordable insurance plans. Sign up in minutes and stay covered." />
  <meta name="keywords" content="Insurance Ghana, Family Insurance, Affordable Cover, Kora Insurance" />
  <meta name="robots" content="noindex, nofollow" />
  <link rel="canonical" href="https://korame.com.gh"/>
  {/* Open Graph tags */}
  <meta property="og:title" content="Best Online Insurance Platform" />
  <meta property="og:description" content="Compare and buy insurance plans with ease." />
  <meta property="og:url" content="https://korame.com.gh/" />
  <meta property="og:type" content="website" />
</Helmet>

const AgentDashboard = () => {
  const navigate = useNavigate();
  const [loggedInAgentId, setLoggedInAgentId] = useState(null);
  const [agent, setAgent] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [totalClients, setTotalClients] = useState(0);
  const [commissionEarned, setCommissionEarned] = useState(0);
  const [bronzePlans, setBronzePlans] = useState(0);
  const [silverPlans, setSilverPlans] = useState(0);
  const [goldPlans, setGoldPlans] = useState(0);
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 768);
  const [showClientListModal, setShowClientListModal] = useState(false);
  const [allClients, setAllClients] = useState([]);



  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setLoggedInAgentId(user.uid);
        await fetchAgentData(user.uid);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // 🔹 Detect screen size changes
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch agent details and their registrations
  const fetchAgentData = async (agentId) => {
    if (!agentId) return;

    // 1️⃣ Fetch Agent Info
    const agentRef = doc(db, "agents", agentId);
    const agentSnap = await getDoc(agentRef);
    if (agentSnap.exists()) {
      setAgent(agentSnap.data());
    }

    // 2️⃣ Fetch All Registrations under the Agent
    const registrationsRef = collection(db, "agents", agentId, "registrations");
    const snapshot = await getDocs(registrationsRef);

    const clients = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));
    
    setAllClients(clients); // ✅ Set full list here
    

    setTotalClients(clients.length); // ✅ Total number of registered clients

    // 3️⃣ Calculate the total commission earned (assuming a commission per registration)
    const totalCommission = clients.length * 1;
    setCommissionEarned(totalCommission);

    // 4️⃣ Count Plans (Bronze, Silver, Gold)
    let bronze = 0, silver = 0, gold = 0;
    clients.forEach(client => {
      if (client.coverOption === "bronze") bronze++;
      else if (client.coverOption === "silver") silver++;
      else if (client.coverOption === "gold") gold++;
    });

    setBronzePlans(bronze);
    setSilverPlans(silver);
    setGoldPlans(gold);

    const sortedClients = clients.sort((a, b) => {
      const timeA = a.timestamp?.seconds || 0;
      const timeB = b.timestamp?.seconds || 0;
      return timeB - timeA; // Descending: newest first
    });

    // 5️⃣ Get Recent 5 Registrations
    setRecentRegistrations(sortedClients.slice(0, 5));
  };

  const handleLogout = async () => {
    try {
      await signOut(getAuth());
      localStorage.removeItem("loginTimestamp");
      navigate("/agent-login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };


  const handleRegisterClient = () => {
    const isLargeScreen = window.innerWidth >= 768;
    if (!loggedInAgentId) {
      alert("Agent ID is missing. Please log in again.");
      return;
    }
    if (isLargeScreen) {
      setShowFormModal(true);
    } else {
      navigate(`/purchase?agentId=${loggedInAgentId}`);
    }
  };

  return (
    <div className="agent-dashboard">
      <div className="dashboard-header">
        {/* Show agent's name dynamically */}
        <h1>Kora Agent Dashboard</h1>

        <div className="dashboard-actions">
          <button className="register-client-btn" onClick={handleRegisterClient}>
            <FiUserPlus className="btn-icon" /> <span className="btn-text">Register New Client</span>
          </button>

          <button className="logout-btn" onClick={handleLogout}>
            <FiLogOut className="logout-icon" />
            <span className="logout-text">Logout</span>
          </button>
        </div>
      </div>
      <hr className="dashboard-divider" />

      <h1>Welcome back, {agent?.fullName || "Agent "} 👋</h1>

      {/*Dashboard Tiles with Dynamic Data */}
      <div className="dashboard-content">
        <div className="dashboard-tiles">
          <div className="tile" onClick={() => setShowClientListModal(true)}>
            <FiUsers className="tile-icon" />
            <div className="tile-info">
              <h3>{totalClients}</h3>
              <p>Total Clients</p>
            </div>
          </div>

          <div className="tile">
            <FiPercent className="tile-icon" />
            <div className="tile-info">
              <h3>GH₵ {commissionEarned.toFixed(2)}</h3>
              <p>Commission Earned</p>
            </div>
          </div>

          <div className="tile">
            <FiTrendingUp className="tile-icon" />
            <div className="tile-info">
              <h3>{bronzePlans} - {silverPlans} - {goldPlans}</h3>
              <p>Bronze | Silver | Gold </p>
            </div>
          </div>
        </div>

        {/* Recent Registrations */}
        <div className="recent-registrations">
          <h2>Recent Registrations</h2>
          {recentRegistrations.length > 0 ? (
            <ul>
              {recentRegistrations.map((client, index) => (
                <li key={index}>{client.firstName} {client.lastName} - {client.coverOption} Plan</li>
              ))}
            </ul>
          ) : (
            <p className="no-registrations">No recent registrations yet.</p>
          )}
        </div>
      </div>
      {/* 🔹 Show Modal ONLY for Large Screens */}
      {isLargeScreen && showFormModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-modal" onClick={() => setShowFormModal(false)}>✖</button>
            <h2>Register a New Client</h2>
            <InsuranceForm agentId={loggedInAgentId} hideHeaderFooter={true} />
            <button
              className="fullscreen-button"
              onClick={() => navigate(`/purchase?agentId=${loggedInAgentId}`)}
            >
              Open in Fullscreen Mode
            </button>
          </div>
        </div>
      )}

      {showClientListModal && (
        <div className="modal-overlay">
          <div className="modal-content client-list-modal">
            <button className="close-modal" onClick={() => setShowClientListModal(false)}>✖</button>
            <h2>All Registered Clients</h2>

            {allClients.length > 0 ? (
              <ul className="client-list">
                {allClients.map((client, index) => (
                  <li key={index} className="client-item">
                    <div className="client-details">
                      <strong>{client.firstName} {client.lastName}</strong> — {client.coverOption} Plan
                    </div>
                    {index !== allClients.length - 1 && <hr className="client-divider" />}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No clients registered yet.</p>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default AgentDashboard;
